import React, { useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Gem, Award, Users } from 'lucide-react';

const MARBLE_COLOR = '#f9ca36';
const MARBLE_GLOW = 'rgba(249,202,54,0.4)';

// ── Confetti Particle System (base: PromotionModal) ───────────
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  size: number;
  shape: 'rect' | 'circle' | 'triangle';
  opacity: number;
  life: number;
  maxLife: number;
}

function createParticle(canvasW: number, baseColor: string): Particle {
  const colors = [baseColor, '#ffffff', '#f9ca36', '#98c136', '#22d3ee', '#fb923c', '#a78bfa'];
  const shapes: Array<'rect' | 'circle' | 'triangle'> = ['rect', 'circle', 'triangle'];
  const maxLife = 180 + Math.random() * 120;

  return {
    x: Math.random() * canvasW,
    y: -10,
    vx: (Math.random() - 0.5) * 4,
    vy: 2 + Math.random() * 4,
    color: colors[Math.floor(Math.random() * colors.length)],
    rotation: Math.random() * Math.PI * 2,
    rotationSpeed: (Math.random() - 0.5) * 0.2,
    size: 4 + Math.random() * 8,
    shape: shapes[Math.floor(Math.random() * shapes.length)],
    opacity: 1,
    life: 0,
    maxLife,
  };
}

function drawParticle(ctx: CanvasRenderingContext2D, p: Particle) {
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(p.rotation);
  ctx.globalAlpha = p.opacity * (1 - p.life / p.maxLife);
  ctx.fillStyle = p.color;

  if (p.shape === 'rect') {
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
  } else if (p.shape === 'circle') {
    ctx.beginPath();
    ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.moveTo(0, -p.size / 2);
    ctx.lineTo(p.size / 2, p.size / 2);
    ctx.lineTo(-p.size / 2, p.size / 2);
    ctx.closePath();
    ctx.fill();
  }

  ctx.restore();
}

// ── Component ──────────────────────────────────────────────────
export interface MarblesGrantModalProps {
  isOpen: boolean;
  /** 'admin' = confirmação de crédito realizado na Manager; 'received' = piloto recebeu marbles */
  mode: 'admin' | 'received';
  amount: number;
  /** apenas modo admin: quantidade de pilotos creditados */
  recipients?: number;
  onClose: () => void;
}

const MarblesGrantModal: React.FC<MarblesGrantModalProps> = ({
  isOpen,
  mode,
  amount,
  recipients,
  onClose,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const particlesRef = useRef<Particle[]>([]);
  const spawnCountRef = useRef(0);

  const startParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    particlesRef.current = [];
    spawnCountRef.current = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (spawnCountRef.current < 200) {
        for (let i = 0; i < 5; i++) {
          particlesRef.current.push(createParticle(canvas.width, MARBLE_COLOR));
        }
        spawnCountRef.current += 5;
      } else if (Math.random() < 0.3) {
        particlesRef.current.push(createParticle(canvas.width, MARBLE_COLOR));
      }

      particlesRef.current = particlesRef.current.filter(p => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;
        p.life++;
        drawParticle(ctx, p);
        return p.life < p.maxLife && p.y < canvas.height + 20;
      });

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();
  }, []);

  const stopParticles = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    particlesRef.current = [];
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => startParticles(), 100);
    } else {
      stopParticles();
    }
    return () => stopParticles();
  }, [isOpen, startParticles, stopParticles]);

  useEffect(() => {
    if (!isOpen) return;
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('marbles_modal.aria_label', 'Marbles')}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
        style={{ animation: 'marbleFadeIn 0.3s ease-out' }}
      />

      {/* Canvas confetti layer */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none z-10"
      />

      {/* Modal panel */}
      <div
        className="relative z-20 w-full max-w-md rounded-2xl overflow-hidden border"
        style={{
          background: 'linear-gradient(160deg, #0d1017 0%, #131720 100%)',
          borderColor: `${MARBLE_COLOR}40`,
          boxShadow: `0 0 60px ${MARBLE_GLOW}, 0 0 120px ${MARBLE_GLOW}50, 0 20px 60px rgba(0,0,0,0.6)`,
          animation: 'marbleModalIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Top accent bar */}
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${MARBLE_COLOR}, transparent)` }}
        />

        {/* Close button */}
        <button
          onClick={onClose}
          id="marbles-modal-close"
          className="absolute top-4 right-4 p-2 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-all z-10"
          aria-label={t('common.close', 'Fechar')}
        >
          <X size={16} />
        </button>

        <div className="p-8 pb-10 text-center">
          {/* Animated icon burst */}
          <div
            className="relative mx-auto mb-6 w-28 h-28 flex items-center justify-center"
            style={{ animation: 'marblePulseGlow 2s ease-in-out infinite' }}
          >
            <div
              className="absolute inset-0 rounded-full border-2 opacity-30"
              style={{ borderColor: MARBLE_COLOR, animation: 'marbleRingExpand 2s ease-in-out infinite' }}
            />
            <div
              className="absolute inset-2 rounded-full border"
              style={{ borderColor: `${MARBLE_COLOR}60`, animation: 'marbleRingExpand 2s 0.3s ease-in-out infinite' }}
            />
            <div
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, rgba(249,202,54,0.15), rgba(249,202,54,0.04))',
                border: `2px solid ${MARBLE_COLOR}60`,
                color: MARBLE_COLOR,
                boxShadow: `0 0 30px ${MARBLE_GLOW}`,
              }}
            >
              <Gem size={36} strokeWidth={2.2} />
            </div>
          </div>

          {/* Title */}
          <div
            className="font-label text-xs font-bold uppercase tracking-[0.3em] mb-2"
            style={{ color: `${MARBLE_COLOR}80` }}
          >
            {mode === 'admin'
              ? t('marbles_modal.admin_header', 'CRÉDITO REALIZADO')
              : t('marbles_modal.received_header', 'BOLSA ATUALIZADA')}
          </div>
          <h2
            className="text-3xl font-display font-black italic uppercase tracking-tight mb-1"
            style={{ color: MARBLE_COLOR, textShadow: `0 0 20px ${MARBLE_GLOW}` }}
          >
            {mode === 'admin'
              ? t('marbles_modal.admin_title', 'MARBLES CREDITADOS')
              : t('marbles_modal.received_title', 'VOCÊ RECEBEU MARBLES')}
          </h2>
          <p className="font-label text-sm text-white/50 mb-6">
            {mode === 'admin'
              ? t('marbles_modal.admin_subtitle',
                  '{{count}} pilotos receberam {{amount}} marbles cada.',
                  { count: recipients ?? 0, amount })
              : t('marbles_modal.received_subtitle',
                  '{{amount}} marbles creditados na sua conta.',
                  { amount })}
          </p>

          {/* Amount visualization */}
          <div
            className="flex items-center justify-center gap-4 p-4 rounded-xl mb-6"
            style={{ background: 'rgba(249,202,54,0.08)' }}
          >
            {mode === 'admin' ? (
              <>
                <div className="text-center">
                  <span className="font-label text-[9px] text-white/30 uppercase tracking-widest block mb-1">
                    {t('marbles_modal.pilots', 'Pilotos')}
                  </span>
                  <span
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-black italic font-display border-2"
                    style={{ color: MARBLE_COLOR, background: 'rgba(249,202,54,0.12)', borderColor: `${MARBLE_COLOR}50` }}
                  >
                    <Users size={24} />
                  </span>
                </div>
                <div className="flex items-center gap-1" style={{ color: MARBLE_COLOR }}>
                  <span className="text-2xl font-black">×</span>
                </div>
                <div className="text-center">
                  <span className="font-label text-[9px] text-white/30 uppercase tracking-widest block mb-1">
                    {t('marbles_modal.each', 'Cada um')}
                  </span>
                  <div
                    className="min-w-[4.5rem] h-14 px-3 rounded-xl flex items-center justify-center text-2xl font-black italic font-display border-2"
                    style={{
                      color: MARBLE_COLOR,
                      background: 'rgba(249,202,54,0.12)',
                      borderColor: `${MARBLE_COLOR}50`,
                      boxShadow: `0 0 20px ${MARBLE_GLOW}`,
                      animation: 'marbleTierPulse 1.5s ease-in-out infinite',
                    }}
                  >
                    {amount}
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <span className="font-label text-[9px] text-white/30 uppercase tracking-widest block mb-1">
                  {t('marbles_modal.balance_received', 'Crédito recebido')}
                </span>
                <div
                  className="min-w-[7rem] h-14 px-4 rounded-xl flex items-center justify-center gap-2 text-3xl font-black italic font-display border-2"
                  style={{
                    color: MARBLE_COLOR,
                    background: 'rgba(249,202,54,0.12)',
                    borderColor: `${MARBLE_COLOR}50`,
                    boxShadow: `0 0 20px ${MARBLE_GLOW}`,
                    animation: 'marbleTierPulse 1.5s ease-in-out infinite',
                  }}
                >
                  <Gem size={22} />
                  {amount}
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <p
            className="text-xs text-white/50 font-label leading-relaxed mb-8"
            style={{ letterSpacing: '0.05em' }}
          >
            {mode === 'admin'
              ? t('marbles_modal.admin_description',
                  'O crédito é aditivo: cada novo clique credita novamente para todos os usuários ativos. Use para testar a dinâmica de eventos monetizados.')
              : t('marbles_modal.received_description',
                  'Use seus marbles para se inscrever em eventos pagos e disputar prêmios na plataforma.')}
          </p>

          {/* CTA */}
          <button
            id="marbles-modal-confirm"
            onClick={onClose}
            className="w-full py-3.5 rounded-xl font-label text-sm font-black uppercase tracking-widest transition-all duration-200 active:scale-95 hover:scale-[1.02]"
            style={{
              background: `linear-gradient(135deg, ${MARBLE_COLOR}CC, ${MARBLE_COLOR}99)`,
              color: '#0a0b0f',
              boxShadow: `0 4px 20px ${MARBLE_GLOW}`,
            }}
          >
            <span className="flex items-center justify-center gap-2">
              <Award size={16} />
              {mode === 'admin'
                ? t('marbles_modal.admin_cta', 'Entendido')
                : t('marbles_modal.received_cta', 'Começar')}
            </span>
          </button>

          {/* Stars decoration */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {[...Array(5)].map((_, i) => (
              <Gem
                key={i}
                size={10}
                fill={MARBLE_COLOR}
                style={{
                  color: MARBLE_COLOR,
                  opacity: 0.6,
                  animation: `marbleTwinkle ${0.5 + i * 0.2}s ease-in-out infinite alternate`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Bottom accent bar */}
        <div
          className="h-px w-full"
          style={{ background: `linear-gradient(90deg, transparent, ${MARBLE_COLOR}40, transparent)` }}
        />
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes marbleFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes marbleModalIn {
          from { opacity: 0; transform: scale(0.7) translateY(30px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes marbleRingExpand {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.1); opacity: 0.1; }
        }
        @keyframes marblePulseGlow {
          0%, 100% { filter: drop-shadow(0 0 10px currentColor); }
          50% { filter: drop-shadow(0 0 25px currentColor); }
        }
        @keyframes marbleTierPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes marbleTwinkle {
          from { opacity: 0.3; transform: scale(0.8); }
          to { opacity: 0.9; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default MarblesGrantModal;
